import { toast } from 'react-toastify'
import { getErrorMessage, getFilenameFromDisposition } from '../../helpers'
import type { BaseApiResponse, QueryProps } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  GetVouchersQuery,
  VoucherMetricsResponse,
  VouchersResponse,
} from './voucher.type'
import {
  CreateDiscount,
  CreateDiscountSuccess,
  DiscountMetricsResponse,
  DiscountsResponse,
  SingleDiscount,
} from '../discounts/discounts.type'

export const vouchersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createDiscount: builder.mutation<CreateDiscountSuccess, CreateDiscount>({
      query: (payload) => ({
        url: `v1/discount`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'DISCOUNT', id: 'LIST' }, 'DISCOUNT'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    editDiscount: builder.mutation<
      CreateDiscountSuccess,
      { id: string; data: CreateDiscount }
    >({
      query: (payload) => ({
        url: `v1/discount/${payload.id}`,
        method: 'PUT',
        body: payload.data,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DISCOUNT', id: arg.id },
        { type: 'DISCOUNT', id: 'LIST' },
        'DISCOUNT',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getDiscounts: builder.query<DiscountsResponse, QueryProps>({
      query: (args) => {
        return {
          url: `v1/discount`,
          method: 'GET',
          params: {
            ...args,
          },
        }
      },
      providesTags: [{ type: 'DISCOUNT', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getSingleDiscounts: builder.query<
      { message: string; success: boolean; data: { discount: SingleDiscount } },
      { id: string }
    >({
      query: ({ id }) => {
        return {
          url: `v1/discount/${id}`,
          method: 'GET',
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'DISCOUNT', id: arg.id },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getDiscountMetrics: builder.query<DiscountMetricsResponse, QueryProps>({
      query: (args) => {
        return {
          url: `v1/discount/discount-metrics`,
          method: 'GET',
          params: {
            ...args,
          },
        }
      },
      providesTags: ['DISCOUNT'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    sendVouchers: builder.mutation<
      CreateDiscountSuccess,
      {
        value: number
        emails: string[]
        template?: string
        subject?: string
      }
    >({
      query: (payload) => ({
        url: `v1/vouchers`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'VOUCHER', id: 'LIST' }, 'VOUCHER'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getVoucherMetrics: builder.query<VoucherMetricsResponse, QueryProps>({
      query: (args) => {
        return {
          url: `v1/vouchers/metrics`,
          method: 'GET',
          params: {
            ...args,
          },
        }
      },
      providesTags: ['VOUCHER'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    getVouchers: builder.query<VouchersResponse, GetVouchersQuery>({
      query: (args) => {
        return {
          url: `v1/vouchers`,
          method: 'GET',
          params: {
            ...args,
          },
        }
      },
      providesTags: [{ type: 'VOUCHER', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    resendVoucher: builder.mutation<
      BaseApiResponse,
      {
        voucherId: string
        recipientEmail?: string
      }
    >({
      query: ({ voucherId, ...payload }) => ({
        url: `v1/vouchers/${voucherId}/resend`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VOUCHER', id: arg.voucherId },
        { type: 'VOUCHER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    activateVoucher: builder.mutation<
      BaseApiResponse,
      {
        voucherId: string
      }
    >({
      query: ({ voucherId, ...payload }) => ({
        url: `v1/vouchers/${voucherId}/reactivate`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VOUCHER', id: arg.voucherId },
        { type: 'VOUCHER', id: 'LIST' },
        'VOUCHER',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    deactivateVoucher: builder.mutation<
      BaseApiResponse,
      {
        voucherId: string
      }
    >({
      query: ({ voucherId, ...payload }) => ({
        url: `v1/vouchers/${voucherId}/deactivate`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'VOUCHER', id: arg.voucherId },
        { type: 'VOUCHER', id: 'LIST' },
        'VOUCHER',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    exportVoucherReport: builder.mutation<
      BaseApiResponse,
      GetVouchersQuery & { fileName: string }
    >({
      query: ({ fileName, ...params }) => {
        return {
          url: `v1/vouchers/export/csv`,
          method: 'GET',
          params: params,
          responseHandler: async (response) => {
            const blob = await response.blob()

            if (blob.type === 'application/json') {
              const text = await blob.text()
              return JSON.parse(text)
            }

            const filename = getFilenameFromDisposition(response, fileName)

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)

            return true
          },
          responseType: 'blob',
        }
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useCreateDiscountMutation,
  useEditDiscountMutation,
  useGetDiscountsQuery,
  useGetSingleDiscountsQuery,
  useLazyGetSingleDiscountsQuery,
  useGetDiscountMetricsQuery,
  useSendVouchersMutation,
  useGetVoucherMetricsQuery,
  useGetVouchersQuery,
  useResendVoucherMutation,
  useActivateVoucherMutation,
  useDeactivateVoucherMutation,
  useExportVoucherReportMutation,
} = vouchersApiSlice
